import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download } from "lucide-react";
import { Button } from "../ui/button";

const navLinks = [
  { href: "#beneficios", label: "Beneficios" },
  { href: "#proceso", label: "Proceso" },
  { href: "#donde-grabamos", label: "Dónde grabamos" },
  { href: "#para-quien", label: "Para Quién" },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass py-3 shadow-sm" : "bg-transparent py-5"
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center" data-testid="logo-link">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 645.4 195.7" className="h-8 w-auto">
              <g fill="#1E293B">
                <path d="M367.6,80.7c-1-2.1,0.3-4.4,2.9-4.4h17.3c1.6,0,2.6,1,2.9,1.9l16,47.4h0.9L437.3,49c0.7-2,3.4-2.4,4.9-0.9L453.2,60c1.3,1.4,1.6,3.6,0.7,5.4l-43.4,94.1c-0.6,1-1.4,1.9-2.9,1.9H406c-1.6,0-2.4-0.9-2.9-1.9L367.6,80.7z"/>
                <path d="M320,59.6c0-1.3,1-2.4,2.4-2.4h12.7c1.3,0,2.4,1.1,2.4,2.4l1.1,76l33.4-2.3c1.4,0,2.4,1.1,2.4,2.4v11.9c0,1.3-1,2.4-2.4,2.4h-49.8c-1.4,0-2.4-1.1-2.4-2.4V59.6H320z"/>
                <path d="M624,59.5c0-1.3,1.1-2.4,2.4-2.4h12.1c1.3,0,2.4,1.1,2.4,2.4v88.1c0,1.3-1.1,2.4-2.4,2.4h-12.1c-1.3,0-2.4-1.1-2.4-2.4V59.5z"/>
                <path d="M538.8,57.9c-0.7-0.7-1.3-0.9-2.3-0.9h-2c-1.3,0-2.4,1-2.4,2.4v88.2c0,1.3,1.1,2.4,2.4,2.4h12c1.4,0,2.4-1.1,2.4-2.4V91.3h0.3l48,57.8c0.7,0.7,1.3,0.9,2.3,0.9h7.6c1.3,0,2.4-1,2.4-2.4v-14.3L538.8,57.9z"/>
                <path d="M609.6,126.1V59.5c0-1.3-1.1-2.4-2.4-2.4h-12c-1.4,0-2.4,1.1-2.4,2.4V102L609.6,126.1z"/>
                <path d="M224,146.6l40.8-88.2c0.4-0.9,1-1.4,2.3-1.4h1.3c1.3,0,1.9,0.7,2.3,1.4l40.4,88.2c0.9,1.7-0.3,3.4-2.3,3.4h-11.4c-2,0-2.9-0.9-3.9-2.6l-7.1-14.1h-37.7l-7.3,14.1c-0.6,1.3-1.7,2.6-3.9,2.6h-11.4C224.2,150,223.2,148.3,224,146.6z M279.6,119.9l-12.1-21.4l-12,21.4H279.6z"/>
                <path d="M225.7,98.4v2.3c0,26-13.3,49.1-41.8,49.1c-26.8,0-43.8-22.7-43.8-48.1c0-26.3,17.4-46.4,45-46.4c14.4,0,27.1,6.7,34.6,18.7c1.1,1.7,0.4,4-1.3,5l-9.6,5.1c-1.7,0.9-3.7,0.3-4.7-1.3c-4.1-6.6-11.1-10.6-19.4-10.6c-17.3,0-25.8,14.1-25.8,29.4s8.7,32.3,26,32.3c11.3,0,20.7-9.7,21-20.8l-16.7-1c-1.3-0.1-2.1-1.1-2.1-2.4V98.4H225.7z"/>
                <path d="M437,146.6l40.8-88.2c0.4-0.9,1-1.4,2.3-1.4h1.3c1.3,0,1.9,0.7,2.3,1.4l40.4,88.2c0.9,1.7-0.3,3.4-2.3,3.4h-11.4c-2,0-2.9-0.9-3.9-2.6l-7.1-14.1h-37.7l-7.3,14.1c-0.6,1.3-1.7,2.6-3.9,2.6h-11.4C437.3,150,436.3,148.3,437,146.6z M492.7,119.9l-12.1-21.4l-12,21.4H492.7z"/>
              </g>
            </svg>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="font-body text-sm text-galvani-textLight hover:text-galvani-blue transition-colors duration-200"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <a href="#solicitar-propuesta">
              <Button
                className="bg-gradient-to-r from-galvani-blue to-galvani-purple hover:opacity-90 text-white font-body font-semibold px-5 py-2 rounded-full transition-all duration-300 shadow-lg shadow-blue-500/25"
                data-testid="header-cta-btn"
              >
                Solicitar propuesta
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-galvani-text p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden mt-4 bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden"
              data-testid="mobile-menu"
            >
              <nav className="flex flex-col py-4">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="px-4 py-3 text-galvani-text hover:text-galvani-blue hover:bg-blue-50 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                ))}
                <div className="px-4 pt-4">
                  <a href="#solicitar-propuesta" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button
                      className="w-full bg-gradient-to-r from-galvani-blue to-galvani-purple text-white font-semibold py-3 rounded-full"
                      data-testid="mobile-cta-btn"
                    >
                      Solicitar propuesta
                    </Button>
                  </a>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
